import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Store, X, Upload } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

/**
 * MarketplaceNewBanner — shown when the builder is opened with
 * ?marketplace=new. Reminds the user they're crafting a marketplace
 * listing and surfaces the draft metadata they entered in SellerDashboard.
 *
 * The PublishToMarketplaceButton in each editor handles the actual
 * submission; this banner is a non-blocking nudge.
 */
export default function MarketplaceNewBanner({ onPublish }) {
  const { t } = useLanguage();
  const [params, setParams] = useSearchParams();
  const [dismissed, setDismissed] = useState(false);
  const isNew = params.get("marketplace") === "new";
  const cloneSource = params.get("clone_source");
  const [draft, setDraft] = useState(null);
  const [cloneSourceName, setCloneSourceName] = useState(null);

  useEffect(() => {
    if (!isNew) return;
    try {
      const d = sessionStorage.getItem("mp_draft");
      if (d) setDraft(JSON.parse(d));
    } catch { /* ignore */ }
  }, [isNew]);

  // Resolve clone-source listing name once so the banner can show provenance.
  useEffect(() => {
    if (!cloneSource) { setCloneSourceName(null); return; }
    const api = process.env.REACT_APP_BACKEND_URL;
    fetch(`${api}/api/marketplace/listings/${cloneSource}`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.name) setCloneSourceName(d.name); })
      .catch(() => { /* silent */ });
  }, [cloneSource]);

  if (!isNew || dismissed) return null;

  const clearDraft = () => {
    try { sessionStorage.removeItem("mp_draft"); } catch {
      /* ignore */
    }
    params.delete("marketplace");
    params.delete("clone_source");
    params.delete("clone_listing_id");
    setParams(params);
    setDismissed(true);
  };

  return (
    <div
      className="flex items-center gap-3 p-3 px-4 mb-3 rounded-xl border"
      style={{
        background: "var(--tint-blue-bg)",
        borderColor: "var(--tint-blue-border)",
      }}
      data-testid="marketplace-new-banner"
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: "var(--tint-blue-border)" }}
      >
        <Store className="w-4 h-4" style={{ color: "var(--tint-blue-icon)" }} />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="text-xs font-semibold flex items-center gap-2 flex-wrap"
          style={{ color: "var(--tint-blue-fg)" }}
        >
          {cloneSource ? (
            <>
              <span>{t("Iterating on a clone")}</span>
              {cloneSourceName && (
                <span
                  className="px-1.5 py-0.5 rounded-md text-[10px] font-mono font-normal"
                  style={{ background: "rgba(16,185,129,0.12)", color: "rgb(110,231,183)" }}
                  data-testid="banner-clone-source"
                >
                  🌿 {cloneSourceName}
                </span>
              )}
            </>
          ) : (
            <>
              {t("Crafting a new marketplace listing")}
              {draft?.name ? ` — "${draft.name}"` : ""}
            </>
          )}
        </div>
        <div
          className="text-[11px] truncate"
          style={{ color: "var(--tint-blue-fg-muted)" }}
        >
          {cloneSource
            ? t("Your clone is saved as a draft. Edit content here, then publish to ship the new version.")
            : t("When you're done, click Publish to Marketplace. We'll pre-fill your title, tags, and price.")}
        </div>
      </div>
      {onPublish && (
        <button
          onClick={onPublish}
          className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-500 transition-colors"
          data-testid="banner-publish-btn"
        >
          <Upload className="w-3 h-3" /> {t("Publish")}
        </button>
      )}
      <button
        onClick={clearDraft}
        className="shrink-0 text-[var(--app-text-muted)] hover:text-[var(--app-text)]"
        data-testid="banner-dismiss-btn"
        title={t("Dismiss")}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/** Hook — expose draft to consumers who want to pre-fill the publish form */
export function useMarketplaceDraft() {
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    try {
      const d = sessionStorage.getItem("mp_draft");
      if (d) setDraft(JSON.parse(d));
    } catch { /* ignore */ }
  }, []);
  return draft;
}
